import React, { useState, useEffect } from 'react';
import {
  X,
  FileText,
  Shield,
  Users,
  Settings,
  BarChart3,
  Mail,
  Eye,
  CheckCircle,
  UserCheck,
  Bell,
  MapPin,
  Palette,
  MessageSquare,
  Database,
  Upload,
  Archive,
} from 'lucide-react';
import api from '../services/api';
import { frontendBuildInfo, formatBuildTimestamp } from '../buildInfo';

function AdminSidebar({
  isOpen,
  onClose,
  activeTab,
  onTabChange,
  user,
  pendingReportsCount = 0,
  onToggleSidebar,
}) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [backendBuild, setBackendBuild] = useState(null);

  useEffect(() => {
    if (!user?.isAdmin) {
      return;
    }

    let cancelled = false;

    api
      .get('/admin/system/build-info')
      .then(response => {
        if (!cancelled) {
          setBackendBuild(response.backend);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setBackendBuild(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [user?.isAdmin]);

  const menuItems = [
    {
      id: 'reports',
      label: 'Report Management',
      icon: FileText,
      description: 'Manage and review incident reports',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
    },
    {
      id: 'archived',
      label: 'Archived Reports',
      icon: Archive,
      description: 'View rejected and archived reports',
      color: 'text-gray-600',
      bgColor: 'bg-gray-50',
      borderColor: 'border-gray-200',
    },
    {
      id: 'moderation',
      label: 'Moderation',
      icon: Eye,
      description: 'Review pending reports and photos',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      badge: pendingReportsCount > 0 ? pendingReportsCount : null,
    },
    {
      id: 'extra-notes',
      label: 'Extra Notes',
      icon: MessageSquare,
      description: 'Review pending extra notes',
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-200',
    },
    {
      id: 'users',
      label: 'User Management',
      icon: Users,
      description: 'Manage user accounts and permissions',
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
    },
    {
      id: 'email',
      label: 'Email Management',
      icon: Mail,
      description: 'Configure email settings and templates',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
    },
    {
      id: 'analytics',
      label: 'Analytics & Export',
      icon: BarChart3,
      description: 'View analytics and export data',
      color: 'text-teal-600',
      bgColor: 'bg-teal-50',
      borderColor: 'border-teal-200',
    },
    {
      id: 'logs-analytics',
      label: 'Logs Analytics',
      icon: BarChart3,
      description: 'Track visitor behavior and site performance',
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
      borderColor: 'border-indigo-200',
    },
  ];

  const superAdminItems = [
    {
      id: 'config',
      label: 'Configuration',
      icon: Settings,
      description: 'System configuration and settings',
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
      borderColor: 'border-indigo-200',
    },
    {
      id: 'area-config',
      label: 'Area & Map Settings',
      icon: MapPin,
      description: 'Manage areas, map restrictions, and heatmap settings',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
    },
    {
      id: 'branding-config',
      label: 'Branding',
      icon: Palette,
      description: 'Customize site appearance and colors',
      color: 'text-pink-600',
      bgColor: 'bg-pink-50',
      borderColor: 'border-pink-200',
    },
    {
      id: 'upload-config',
      label: 'Upload Settings',
      icon: Upload,
      description: 'Configure photo upload limits and resizing',
      color: 'text-cyan-600',
      bgColor: 'bg-cyan-50',
      borderColor: 'border-cyan-200',
    },
    {
      id: 'database-management',
      label: 'Database Management',
      icon: Database,
      description: 'Manage database operations and maintenance',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
    },
    {
      id: 'super-users',
      label: 'Super User Management',
      icon: UserCheck,
      description: 'Manage super admin privileges',
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
    },
    {
      id: 'error-logs',
      label: 'Log Viewer',
      icon: FileText,
      description: 'View and manage application logs',
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
    },
    {
      id: 'analytics',
      label: 'Analytics Dashboard',
      icon: BarChart3,
      description: 'Track visitor behavior and site performance',
      color: 'text-teal-600',
      bgColor: 'bg-teal-50',
      borderColor: 'border-teal-200',
    },
    {
      id: 'system-info',
      label: 'System Info',
      icon: Settings,
      description: 'System information and security check',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      superAdminOnly: true,
    },
  ];

  const handleTabClick = tabId => {
    onTabChange(tabId);
    // On mobile, close the sidebar after selecting a tab
    if (window.innerWidth < 768) {
      onClose();
    }
  };

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden" onClick={onClose} />
      )}

      {/* Sidebar */}
      <div
        className={`
        fixed top-0 left-0 h-full bg-white shadow-xl z-50 transition-all duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        ${isCollapsed ? 'w-16' : 'w-80'}
        md:translate-x-0 md:relative md:z-auto
        max-w-full overflow-x-hidden
      `}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-primary-600 to-primary-700 text-white">
          {!isCollapsed && (
            <div className="flex items-center space-x-3">
              <Shield className="w-6 h-6" />
              <div>
                <h2 className="font-bold text-lg">Admin Panel</h2>
                <p className="text-xs text-primary-100">Management Console</p>
              </div>
            </div>
          )}
          <div className="flex items-center space-x-2">
            <button
              onClick={toggleCollapse}
              className="p-1 rounded hover:bg-white hover:bg-opacity-20 transition-colors"
              title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 5l7 7-7 7M5 5l7 7-7 7"
                />
              </svg>
            </button>
            <button
              onClick={onClose}
              className="p-1 rounded hover:bg-white hover:bg-opacity-20 transition-colors md:hidden"
              title="Close sidebar"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 overflow-x-hidden max-w-full">
          {/* Regular Admin Items */}
          <div className="px-4 mb-6">
            {!isCollapsed && (
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Admin Functions
              </h3>
            )}
            <ul className="space-y-1">
              {menuItems
                .filter(item => !item.superAdminOnly || (user && user.isSuperAdmin))
                .map(item => {
                  const Icon = item.icon;
                  return (
                    <li key={item.id}>
                      <button
                        onClick={() => handleTabClick(item.id)}
                        className={`
                        w-full flex items-center px-3 py-2 rounded-lg transition-all duration-200
                        ${
                          activeTab === item.id
                            ? `${item.bgColor} ${item.color} border ${item.borderColor}`
                            : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                        }
                        ${isCollapsed ? 'justify-center' : 'justify-start'}
                      `}
                        title={isCollapsed ? item.label : undefined}
                      >
                        <Icon className={`w-5 h-5 ${isCollapsed ? '' : 'mr-3'}`} />
                        {!isCollapsed && (
                          <div className="flex-1 text-left">
                            <div className="flex items-center justify-between">
                              <span className="font-medium truncate max-w-full block">
                                {item.label}
                              </span>
                              {item.badge && (
                                <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                                  {item.badge}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 mt-1 break-words overflow-hidden text-ellipsis max-w-full">
                              {item.description}
                            </p>
                          </div>
                        )}
                      </button>
                    </li>
                  );
                })}
            </ul>
          </div>

          {/* Super Admin Items */}
          {user && user.isSuperAdmin && (
            <div className="px-4 mb-6">
              {!isCollapsed && (
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                  Super Admin
                </h3>
              )}
              <ul className="space-y-1">
                {superAdminItems
                  .filter(item => !menuItems.some(adminItem => adminItem.id === item.id))
                  .map(item => {
                    const Icon = item.icon;
                    return (
                      <li key={item.id}>
                        <button
                          onClick={() => handleTabClick(item.id)}
                          className={`
                          w-full flex items-center px-3 py-2 rounded-lg transition-all duration-200
                          ${
                            activeTab === item.id
                              ? `${item.bgColor} ${item.color} border ${item.borderColor}`
                              : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                          }
                          ${isCollapsed ? 'justify-center' : 'justify-start'}
                        `}
                          title={isCollapsed ? item.label : undefined}
                        >
                          <Icon className={`w-5 h-5 ${isCollapsed ? '' : 'mr-3'}`} />
                          {!isCollapsed && (
                            <div className="flex-1 text-left">
                              <div className="flex items-center justify-between">
                                <span className="font-medium">{item.label}</span>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">{item.description}</p>
                            </div>
                          )}
                        </button>
                      </li>
                    );
                  })}
              </ul>
            </div>
          )}

          {/* Quick Actions */}
          {!isCollapsed && (
            <div className="px-4 mb-6">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Quick Actions
              </h3>
              <div className="space-y-2">
                <button
                  className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  onClick={() => handleTabClick('reports')}
                >
                  <CheckCircle className="w-4 h-4 mr-3 text-green-600" />
                  <span>Review Pending</span>
                </button>
                <button
                  className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  onClick={() => handleTabClick('users')}
                >
                  <UserCheck className="w-4 h-4 mr-3 text-blue-600" />
                  <span>Manage Users</span>
                </button>
                <button
                  className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  onClick={() => handleTabClick('email')}
                >
                  <Bell className="w-4 h-4 mr-3 text-purple-600" />
                  <span>Email Settings</span>
                </button>
              </div>
            </div>
          )}
        </nav>

        {/* Footer */}
        {!isCollapsed && (
          <div className="p-4 border-t border-gray-200">
            <div className="text-xs text-gray-500 space-y-1">
              <p className="font-medium text-gray-700">Deployment</p>
              <p>
                API v{backendBuild?.appVersion || '—'} · {backendBuild?.imageTag || 'local'}
              </p>
              <p>
                Web v{frontendBuildInfo.appVersion} · {frontendBuildInfo.imageTag}
              </p>
              <p className="font-mono">
                {backendBuild?.buildShaShort || frontendBuildInfo.buildShaShort}
              </p>
              <p title={backendBuild?.containerStartedAt || ''}>
                Running since {formatBuildTimestamp(backendBuild?.containerStartedAt)}
              </p>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default AdminSidebar;
